import React from 'react';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import CheckIcon from '@mui/icons-material/Check';
import StarIcon from '@mui/icons-material/Star';
import BoltIcon from '@mui/icons-material/Bolt';
import { FadeIn } from './FadeIn';
import { SectionHeader } from './SectionHeader';
import { AnimatedBackground } from './AnimatedBackground';
import { LiveActivityPanel } from './live/LiveActivityPanel';
import { packages } from '../data/packages';
import { scrollToForm } from '../utils/scroll';
import type { PackagePlan, PackageTone } from '../types';
interface ToneStyle {
  card: string;
  name: string;
  price: string;
  tag: string;
  check: string;
  cta: string;
}
const toneStyles: Record<PackageTone, ToneStyle> = {
  blue: {
    card: 'border-brand-blue/30 hover:border-brand-blue',
    name: 'text-brand-blue',
    price: 'text-brand-blue',
    tag: 'bg-brand-blue/10 text-brand-blue',
    check: 'bg-brand-blue',
    cta: 'bg-brand-blue text-white hover:bg-brand-dark'
  },
  yellow: {
    card: 'border-brand-yellow',
    name: 'text-[#B98900]',
    price: 'text-[#B98900]',
    tag: 'bg-brand-yellow/20 text-[#8A6A00]',
    check: 'bg-[#B98900]',
    cta: 'bg-brand-yellow text-brand-deep hover:bg-brand-yellow-dark'
  },
  green: {
    card: 'border-brand-green/40 hover:border-brand-green',
    name: 'text-[#16883F]',
    price: 'text-[#16883F]',
    tag: 'bg-brand-green/15 text-[#16883F]',
    check: 'bg-brand-green',
    cta: 'bg-brand-green text-[#06210F] hover:bg-[#1BAA51]'
  }
};
export function Packages() {
  return <section id="paquetes" className="relative w-full overflow-hidden bg-[linear-gradient(160deg,#071F5C_0%,#0A2A78_55%,#071F5C_100%)] px-6 py-24" data-id="element-205" data-mp="24z6bt2sio16b">
      <AnimatedBackground intensity="soft" icons data-id="element-206" data-mp="1w3qnf1aj6e84" />

      <div className="relative mx-auto max-w-[1180px]" data-id="element-207" data-mp="2sm631pi35yvk">
        <FadeIn data-id="element-208" data-mp="2snzerj7k39af">
          <SectionHeader dark centered badge="Paquetes" badgeIcon={<PaymentsOutlinedIcon sx={{
          fontSize: 15
        }} data-id="element-210" data-mp="2ntv4apz30j4l" />} title="Precio claro. Sin sorpresas." subtitle="Elige el paquete que se adapta a tu negocio. Todos incluyen entrega en 7 días y soporte completo." data-id="element-209" data-mp="2soiv4vhk5pmc" />
        </FadeIn>

        <div className="flex flex-col gap-7 xl:flex-row xl:items-stretch" data-id="element-211" data-mp="c7kvio0vktz5">
          <div className="grid flex-1 grid-cols-1 items-end gap-5 sm:grid-cols-3" data-id="element-212" data-mp="h6xegxomlgwg">
            {packages.map((plan, index) => <FadeIn key={plan.id} delay={index * 0.1} className="h-full" data-id="element-213" data-mp="36uzbj7pd1kw" data-mpi={index}>
                <PackageCard plan={plan} index={index} data-id="element-214" data-mp="870x7h4kfkbf" />
              </FadeIn>)}
          </div>

          <FadeIn direction="left" delay={0.15} data-id="element-215" data-mp="3pfxp7ypzeykh">
            <LiveActivityPanel data-id="element-216" data-mp="3ph8pvpo4rq7d" />
          </FadeIn>
        </div>
      </div>
    </section>;
}
function PackageCard({
  plan,
  index
}: {
  plan: PackagePlan;
  index: number;
}) {
  const tone = toneStyles[plan.tone];
  const isEssential = plan.tone === 'blue';
  const isElite = plan.tone === 'green';
  return <article style={isEssential || isElite ? {
    animationDelay: `${index * 1.2}s`
  } : undefined} className={`relative flex h-full flex-col rounded-[22px] border-2 bg-white p-6 shadow-[0_20px_40px_rgba(0,0,0,0.28)] transition-colors duration-300 ${tone.card} ${isElite ? 'overflow-hidden' : ''} ${isEssential || isElite ? 'animate-float' : ''} ${plan.popular ? 'animate-glow-card pt-8 sm:-translate-y-4' : ''}`} data-id="element-217" data-mp="3d4mwy0psyawf">
      {plan.popular ? <span className="absolute -top-3.5 left-1/2 z-10 flex -translate-x-1/2 animate-badge-bounce items-center gap-1 whitespace-nowrap rounded-full bg-brand-yellow px-4 py-1.5 text-[10.5px] font-extrabold uppercase tracking-wide text-brand-deep shadow-lg" data-id="element-218" data-mp="3vwz739mp6gh4">
          <StarIcon sx={{
        fontSize: 12
      }} data-id="element-219" data-mp="4q4uqfmjlpkq" />
          El Más Popular
        </span> : null}

      {isElite ? <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-0 w-1/2 animate-sweep-x bg-[linear-gradient(120deg,transparent,rgba(34,197,94,0.22),transparent)]" data-id="element-220" data-mp="2bfjh5l6afoq5" /> : null}

      <p className={`relative mb-2 text-[12px] font-bold uppercase tracking-[0.14em] ${tone.name}`} data-id="element-221" data-mp="2cwgf0w2vztrt">
        {plan.name}
      </p>

      <p className={`relative font-display text-[34px] font-extrabold leading-none ${tone.price}`} data-id="element-222" data-mp="2hwuo8hg859rf">
        {plan.price}
      </p>
      <p className="relative mb-2 mt-1 text-[11px] text-muted" data-id="element-223" data-mp="1p6vw2d6qgwij">
        pago {plan.priceNote}
      </p>

      <span className={`relative mb-4 inline-flex w-fit items-center gap-1 rounded-lg px-2.5 py-1 text-[10.5px] font-bold ${tone.tag}`} data-id="element-224" data-mp="1u5yrr70tpqdu">
        <BoltIcon sx={{
        fontSize: 13
      }} data-id="element-225" data-mp="1z3h1v0qkubnf" />
        {plan.delivery}
      </span>

      <ul className="relative mb-6 flex flex-col gap-2" data-id="element-226" data-mp="23wtox6flyae2">
        {plan.features.map(feature => <li key={feature} className="flex items-start gap-2 text-[12.5px] leading-snug text-[#33406B]" data-id="element-227" data-mp="16fbhif3fqoo4">
            <span className={`mt-0.5 flex h-[17px] w-[17px] flex-shrink-0 items-center justify-center rounded-full text-white ${tone.check}`} data-id="element-228" data-mp="1bdcx2q1nk5uu">
              <CheckIcon sx={{
            fontSize: 11
          }} data-id="element-229" data-mp="3kpgmhqxac3rw" />
            </span>
            {feature}
          </li>)}
      </ul>

      <button type="button" onClick={scrollToForm} className={`relative mt-auto w-full rounded-[13px] px-4 py-3 font-display text-[13px] font-bold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2 ${tone.cta} ${plan.popular ? 'animate-ring-pulse' : ''}`} data-id="element-230" data-mp="3fr5ellqjw3cb">
        Quiero Este Paquete
      </button>
    </article>;
}